import React from 'react';
import { Button, Flex, RiAddBoxLine } from '@itandi/itandi-bb-ui';
import Styles from './ConstructionTop.module.scss';
import { toThousandSeparator } from '~/utils/CurrencySeparator';
import {
  DealConstructionContext,
  isDealBillingContext
} from '~/models/DealConstruction';
import { MEDIA_QUERY, useMediaQuery } from '~/hooks/useMediaQuery';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { getDownloadPath } from '~/utils/api/deals/DealSettlementFile';

type Props = {
  readonly: boolean;
  constructionContext: DealConstructionContext;
  dealEstimationHistoryContext?: DealConstructionContext | null;
  dealEstimationDraftHeader?: DealConstructionContext | null;
  handleClickEstimationPreview?: (() => void) | null;
  handleClickEstimationHistoryPreview?: (() => void) | null;
  handleClickBillingPreview?: (() => void) | null;
  handleClickShowAddModal?: (() => void) | null;
  handleClickChangeAmount?: (() => void) | null;
  handleClickGenerateBillings?: (() => void) | null;
  handleClickDestroyHeader?: (() => void) | null;
  billingTarget?: 'owner' | 'rentee' | null;
  useSK?: boolean;
  estimationAddDisabled?: boolean;
  ownManagement?: boolean;
};

export const ConstructionTop: React.FC<Props> = ({
  readonly,
  constructionContext,
  dealEstimationHistoryContext = null,
  dealEstimationDraftHeader = null,
  handleClickEstimationPreview = null,
  handleClickEstimationHistoryPreview = null,
  handleClickBillingPreview = null,
  handleClickShowAddModal = null,
  handleClickChangeAmount = null,
  handleClickGenerateBillings = null,
  handleClickDestroyHeader = null,
  billingTarget = null,
  useSK = false,
  estimationAddDisabled = false,
  ownManagement = false
}: Props) => {
  const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
  const { totalAmount, totalAmountWithTax } = constructionContext;
  const id = useIdQueryHandler();
  const showSettlementInformation =
    isDealBillingContext(constructionContext) &&
    constructionContext.dealSettlements.length > 0;
  const handleClickSettlementPreview = (): void => {
    if (id === null || billingTarget === null) return;
    const path = getDownloadPath(id, billingTarget);
    window.open(path);
  };
  const handleClickEstimationPreviewDisabled = ((): boolean => {
    if (ownManagement && dealEstimationDraftHeader) {
      return (
        dealEstimationDraftHeader.dealConstructions.length === 0 &&
        constructionContext.dealConstructions.length === 0
      );
    }
    return constructionContext.dealConstructions.length === 0;
  })();
  const showEstimationHistory = dealEstimationHistoryContext && !ownManagement;
  const totalAmountLabel = ((): string => {
    if (billingTarget) return '施工項目総額';
    if (showEstimationHistory) return '申請前総額';
    return '総額';
  })();
  const getTotalAmountWithTax = ((): number => {
    // 非共有案件に申請前、申請後という概念は存在しないので、常に最新の見積り額を表示する
    if (
      dealEstimationDraftHeader &&
      dealEstimationDraftHeader.totalAmountWithTax !== 0 &&
      ownManagement
    ) {
      return dealEstimationDraftHeader.totalAmountWithTax;
    }
    return totalAmountWithTax;
  })();
  const getTotalAmount = ((): number => {
    if (
      dealEstimationDraftHeader &&
      dealEstimationDraftHeader.totalAmount !== 0 &&
      ownManagement
    ) {
      return dealEstimationDraftHeader.totalAmount;
    }
    return totalAmount;
  })();
  if (!windowLoaded) return null;
  return (
    <div className={Styles.EstimateTop}>
      <Flex direction="column" gap="8px">
        {showSettlementInformation && (
          <div className={Styles.Amounts}>
            <span className={Styles.TotalAmount}>
              精算項目総額：¥
              {toThousandSeparator(
                billingTarget === 'owner'
                  ? constructionContext.settlementOwnerTotalAmountWithTax
                  : constructionContext.settlementRenteeTotalAmountWithTax
              )}
            </span>
            <span className={Styles.TotalAmountWithoutTax}>
              税抜：¥
              {toThousandSeparator(
                billingTarget === 'owner'
                  ? constructionContext.settlementOwnerTotalAmount
                  : constructionContext.settlementRenteeTotalAmount
              )}
            </span>
          </div>
        )}
        <Flex direction="column" gap="8px">
          {showEstimationHistory && (
            <div className={Styles.Amounts}>
              <span className={Styles.TotalAmount}>
                申請後総額：¥
                {toThousandSeparator(
                  dealEstimationHistoryContext.totalAmountWithTax
                )}
              </span>
              <span className={Styles.TotalAmountWithoutTax}>
                税抜：¥
                {toThousandSeparator(dealEstimationHistoryContext.totalAmount)}
              </span>
            </div>
          )}
          <div className={Styles.Amounts}>
            <span className={Styles.TotalAmount}>
              {totalAmountLabel}：¥
              {toThousandSeparator(getTotalAmountWithTax)}
            </span>
            <span className={Styles.TotalAmountWithoutTax}>
              税抜：¥
              {toThousandSeparator(getTotalAmount)}
            </span>
          </div>
        </Flex>
      </Flex>
      <Flex
        direction={isSp ? 'column' : 'row'}
        gap="8px"
        marginLeft="auto"
        paddingX="8px"
        width={isSp ? '100%' : 'auto'}
      >
        {handleClickChangeAmount !== null && (
          <Button
            onClick={handleClickChangeAmount}
            variant="tertiary"
            width={isSp ? 'fill' : 'default'}
          >
            施工単価一括変更
          </Button>
        )}
        {handleClickGenerateBillings && (
          <Button
            onClick={handleClickGenerateBillings}
            variant="tertiary"
            width="default"
          >
            請求再作成
          </Button>
        )}
        {!useSK && billingTarget !== null && (
          <Button
            disabled={!showSettlementInformation}
            onClick={handleClickSettlementPreview}
            variant="secondary"
            width={isSp ? 'fill' : 'default'}
          >
            精算書ダウンロード
          </Button>
        )}
        {billingTarget === null &&
          dealEstimationDraftHeader &&
          handleClickDestroyHeader &&
          !ownManagement && (
            <Button
              disabled={
                dealEstimationDraftHeader.dealConstructions.length === 0
              }
              onClick={handleClickDestroyHeader}
              variant="tertiary"
              width={isSp ? 'fill' : 'default'}
            >
              見積りを元に戻す
            </Button>
          )}
        {handleClickBillingPreview && billingTarget !== null && (
          <Button
            disabled={constructionContext.dealConstructions.length === 0}
            onClick={handleClickBillingPreview}
            variant="secondary"
            width={isSp ? 'fill' : 'default'}
          >
            請求書ダウンロード
          </Button>
        )}
        {handleClickEstimationPreview && (
          <Button
            disabled={handleClickEstimationPreviewDisabled}
            onClick={handleClickEstimationPreview}
            variant="secondary"
            width={isSp ? 'fill' : 'default'}
          >
            {showEstimationHistory
              ? '申請前見積書ダウンロード'
              : '見積書ダウンロード'}
          </Button>
        )}
        {handleClickEstimationHistoryPreview && !ownManagement && (
          <Button
            disabled={
              dealEstimationDraftHeader?.dealConstructions.length === 0 &&
              dealEstimationHistoryContext?.dealConstructions.length === 0
            }
            onClick={handleClickEstimationHistoryPreview}
            variant="secondary"
            width={isSp ? 'fill' : 'default'}
          >
            {dealEstimationHistoryContext
              ? '申請後見積書ダウンロード'
              : '見積書ダウンロード'}
          </Button>
        )}
        {handleClickShowAddModal !== null && !readonly && (
          <div className={Styles.AddButton}>
            <Button
              disabled={estimationAddDisabled}
              onClick={handleClickShowAddModal}
              startIcon={<RiAddBoxLine />}
              variant="secondary"
              width={isSp ? 'fill' : 'default'}
            >
              項目追加
            </Button>
          </div>
        )}
      </Flex>
    </div>
  );
};
